import { catchError, Observable, throwError, timeout } from 'rxjs'
import { fromFetch } from 'rxjs/fetch'

export interface XmlParty {
	name?: string
	address: string[]
	vatId?: string
	taxId?: string
}
export interface XmlLine {
	id?: string
	name?: string
	description?: string
	quantity?: string
	unit?: string
	unitPrice?: string
	priceBasis?: string
	priceBasisUnit?: string
	net?: string
	taxRate?: string
}
export interface XmlTax {
	category?: string
	rate?: string
	basis?: string
	amount?: string
	exemption?: string
}
export interface XmlInvoice {
	syntax: 'CII' | 'UBL'
	typeCode?: string
	number?: string
	issueDate?: string
	dueDate?: string
	currency?: string
	seller: XmlParty
	buyer: XmlParty
	buyerReference?: string
	orderReference?: string
	notes: string[]
	lines: XmlLine[]
	taxes: XmlTax[]
	totals: {
		net?: string
		tax?: string
		gross?: string
		prepaid?: string
		due?: string
		allowance?: string
		charge?: string
	}
	payment: {
		means?: string
		reference?: string
		terms?: string
		iban?: string
		accountId?: string
		bic?: string
		bankIdentifier?: string
		accountName?: string
	}
}
export type XmlDocument =
	{ kind: 'invoice'; source: string; invoice: XmlInvoice } | { kind: 'xml'; source: string; rootName: string }
export class XmlPreviewError extends Error {
	constructor(public readonly code: 'unsafe' | 'malformed' | 'too-large' | 'unreadable' | 'request') {
		super(code)
		this.name = 'XmlPreviewError'
	}
}

const limit = 2 * 1024 * 1024
const rsm = 'urn:un:unece:uncefact:data:standard:CrossIndustryInvoice:100'
const ram = 'urn:un:unece:uncefact:data:standard:ReusableAggregateBusinessInformationEntity:100'
const udt = 'urn:un:unece:uncefact:data:standard:UnqualifiedDataType:100'
const cac = 'urn:oasis:names:specification:ubl:schema:xsd:CommonAggregateComponents-2'
const cbc = 'urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2'

function children(parent: Element | undefined, namespace: string, name: string): Element[] {
	return parent
		? Array.from(parent.children).filter(child => child.namespaceURI === namespace && child.localName === name)
		: []
}
function child(parent: Element | undefined, namespace: string, name: string) {
	return children(parent, namespace, name)[0]
}
function text(element: Element | undefined): string | undefined {
	if (!element || element.children.length) return undefined
	return element.textContent?.trim() || undefined
}
function value(parent: Element | undefined, namespace: string, name: string) {
	return text(child(parent, namespace, name))
}
function money(parent: Element | undefined, namespace: string, name: string, currency: string | undefined) {
	return text(
		children(parent, namespace, name).find(
			amount => !amount.hasAttribute('currencyID') || amount.getAttribute('currencyID') === currency,
		),
	)
}
function date(parent: Element | undefined): string | undefined {
	const element = child(parent, udt, 'DateTimeString')
	const literal = text(element)
	if (element?.getAttribute('format') === '102' && literal && /^\d{8}$/.test(literal))
		return `${literal.slice(0, 4)}-${literal.slice(4, 6)}-${literal.slice(6)}`
	return literal
}
function ciiParty(party: Element | undefined): XmlParty {
	const address = child(party, ram, 'PostalTradeAddress')
	const registrations = children(party, ram, 'SpecifiedTaxRegistration').flatMap(registration =>
		children(registration, ram, 'ID'),
	)
	return {
		name: value(party, ram, 'Name'),
		address: [
			'LineOne',
			'LineTwo',
			'LineThree',
			'PostcodeCode',
			'CityName',
			'CountrySubDivisionName',
			'CountryID',
		].flatMap(name => value(address, ram, name) ?? []),
		vatId: text(registrations.find(id => id.getAttribute('schemeID') === 'VA')),
		taxId: text(registrations.find(id => id.getAttribute('schemeID') === 'FC')),
	}
}
function ublParty(container: Element | undefined): XmlParty {
	const party = child(container, cac, 'Party')
	const address = child(party, cac, 'PostalAddress')
	const taxSchemes = children(party, cac, 'PartyTaxScheme')
	return {
		name:
			value(child(party, cac, 'PartyLegalEntity'), cbc, 'RegistrationName') ??
			value(child(party, cac, 'PartyName'), cbc, 'Name'),
		address: [
			value(address, cbc, 'StreetName'),
			value(address, cbc, 'AdditionalStreetName'),
			...children(address, cac, 'AddressLine').map(line => value(line, cbc, 'Line')),
			value(address, cbc, 'PostalZone'),
			value(address, cbc, 'CityName'),
			value(address, cbc, 'CountrySubentity'),
			value(child(address, cac, 'Country'), cbc, 'IdentificationCode'),
		].flatMap(line => line ?? []),
		vatId: value(
			taxSchemes.find(scheme => value(child(scheme, cac, 'TaxScheme'), cbc, 'ID') === 'VAT'),
			cbc,
			'CompanyID',
		),
		taxId: value(
			taxSchemes.find(scheme => value(child(scheme, cac, 'TaxScheme'), cbc, 'ID') !== 'VAT'),
			cbc,
			'CompanyID',
		),
	}
}

/** Reads namespace-qualified business paths; source values are never recalculated or validated as an invoice. */
export function parseXmlDocument(source: string): XmlDocument {
	if (source.length > limit || new TextEncoder().encode(source).byteLength > limit)
		throw new XmlPreviewError('too-large')
	if (
		/<!\s*(?:DOCTYPE|ENTITY)\b|<\?xml-stylesheet\b/i.test(
			source.replace(/<!--[\s\S]*?-->|<!\[CDATA\[[\s\S]*?\]\]>/g, ''),
		)
	)
		throw new XmlPreviewError('unsafe')
	// The terminal PI proves the parser reached the document end outside its root;
	// a user element named parsererror is ordinary XML, in any namespace.
	const document = new DOMParser().parseFromString(`${source}<?schmancy-preview-complete?>`, 'application/xml')
	if (document.lastChild?.nodeType !== Node.PROCESSING_INSTRUCTION_NODE || document.lastChild.nodeName !== 'schmancy-preview-complete' || !document.documentElement)
		throw new XmlPreviewError('malformed')
	if (document.getElementsByTagNameNS('http://www.w3.org/2001/XInclude', '*').length)
		throw new XmlPreviewError('unsafe')
	const root = document.documentElement
	if (root.namespaceURI === rsm && root.localName === 'CrossIndustryInvoice') {
		const header = child(root, rsm, 'ExchangedDocument')
		const transaction = child(root, rsm, 'SupplyChainTradeTransaction')
		const agreement = child(transaction, ram, 'ApplicableHeaderTradeAgreement')
		const settlement = child(transaction, ram, 'ApplicableHeaderTradeSettlement')
		const totals = child(settlement, ram, 'SpecifiedTradeSettlementHeaderMonetarySummation')
		const terms = child(settlement, ram, 'SpecifiedTradePaymentTerms')
		const means = child(settlement, ram, 'SpecifiedTradeSettlementPaymentMeans')
		const account = child(means, ram, 'PayeePartyCreditorFinancialAccount')
		const currency = value(settlement, ram, 'InvoiceCurrencyCode')
		return {
			kind: 'invoice',
			source,
			invoice: {
				syntax: 'CII',
				typeCode: value(header, ram, 'TypeCode'),
				number: value(header, ram, 'ID'),
				issueDate: date(child(header, ram, 'IssueDateTime')),
				dueDate: date(child(terms, ram, 'DueDateDateTime')),
				currency,
				seller: ciiParty(child(agreement, ram, 'SellerTradeParty')),
				buyer: ciiParty(child(agreement, ram, 'BuyerTradeParty')),
				buyerReference: value(agreement, ram, 'BuyerReference'),
				orderReference: value(child(agreement, ram, 'BuyerOrderReferencedDocument'), ram, 'IssuerAssignedID'),
				notes: children(header, ram, 'IncludedNote').flatMap(note => value(note, ram, 'Content') ?? []),
				lines: children(transaction, ram, 'IncludedSupplyChainTradeLineItem').map(line => {
					const product = child(line, ram, 'SpecifiedTradeProduct')
					const quantity = child(child(line, ram, 'SpecifiedLineTradeDelivery'), ram, 'BilledQuantity')
					const lineSettlement = child(line, ram, 'SpecifiedLineTradeSettlement')
					const price = child(child(line, ram, 'SpecifiedLineTradeAgreement'), ram, 'NetPriceProductTradePrice')
					const basis = child(price, ram, 'BasisQuantity')
					return {
						id: value(child(line, ram, 'AssociatedDocumentLineDocument'), ram, 'LineID'),
						name: value(product, ram, 'Name'),
						description: value(product, ram, 'Description'),
						quantity: text(quantity),
						unit: quantity?.getAttribute('unitCode') || undefined,
						unitPrice: money(price, ram, 'ChargeAmount', currency),
						priceBasis: text(basis),
						priceBasisUnit: basis?.getAttribute('unitCode') || undefined,
						net: money(
							child(lineSettlement, ram, 'SpecifiedTradeSettlementLineMonetarySummation'),
							ram,
							'LineTotalAmount',
							currency,
						),
						taxRate: value(
							children(lineSettlement, ram, 'ApplicableTradeTax').find(tax => value(tax, ram, 'TypeCode') === 'VAT'),
							ram,
							'RateApplicablePercent',
						),
					}
				}),
				taxes: children(settlement, ram, 'ApplicableTradeTax')
					.filter(tax => value(tax, ram, 'TypeCode') === 'VAT')
					.map(tax => ({
						category: value(tax, ram, 'CategoryCode'),
						rate: value(tax, ram, 'RateApplicablePercent'),
						basis: money(tax, ram, 'BasisAmount', currency),
						amount: money(tax, ram, 'CalculatedAmount', currency),
						exemption: value(tax, ram, 'ExemptionReason'),
					})),
				totals: {
					net: money(totals, ram, 'TaxBasisTotalAmount', currency),
					tax: money(totals, ram, 'TaxTotalAmount', currency),
					gross: money(totals, ram, 'GrandTotalAmount', currency),
					prepaid: money(totals, ram, 'TotalPrepaidAmount', currency),
					due: money(totals, ram, 'DuePayableAmount', currency),
					allowance: money(totals, ram, 'AllowanceTotalAmount', currency),
					charge: money(totals, ram, 'ChargeTotalAmount', currency),
				},
				payment: {
					means: value(means, ram, 'TypeCode'),
					reference: value(settlement, ram, 'PaymentReference'),
					terms: value(terms, ram, 'Description'),
					iban: value(account, ram, 'IBANID'),
					accountName: value(account, ram, 'AccountName'),
					bic: value(child(means, ram, 'PayeeSpecifiedCreditorFinancialInstitution'), ram, 'BICID'),
				},
			},
		}
	}
	if (
		(root.localName === 'Invoice' && root.namespaceURI === 'urn:oasis:names:specification:ubl:schema:xsd:Invoice-2') ||
		(root.localName === 'CreditNote' &&
			root.namespaceURI === 'urn:oasis:names:specification:ubl:schema:xsd:CreditNote-2')
	) {
		const credit = root.localName === 'CreditNote'
		const currency = value(root, cbc, 'DocumentCurrencyCode')
		const totals = child(root, cac, 'LegalMonetaryTotal')
		const taxTotal = children(root, cac, 'TaxTotal').find(
			total => child(total, cbc, 'TaxAmount')?.getAttribute('currencyID') === currency,
		)
		const means = child(root, cac, 'PaymentMeans')
		const account = child(means, cac, 'PayeeFinancialAccount')
		return {
			kind: 'invoice',
			source,
			invoice: {
				syntax: 'UBL',
				typeCode: value(root, cbc, credit ? 'CreditNoteTypeCode' : 'InvoiceTypeCode') ?? (credit ? '381' : undefined),
				number: value(root, cbc, 'ID'),
				issueDate: value(root, cbc, 'IssueDate'),
				dueDate: value(root, cbc, 'DueDate'),
				currency,
				seller: ublParty(child(root, cac, 'AccountingSupplierParty')),
				buyer: ublParty(child(root, cac, 'AccountingCustomerParty')),
				buyerReference: value(root, cbc, 'BuyerReference'),
				orderReference: value(child(root, cac, 'OrderReference'), cbc, 'ID'),
				notes: children(root, cbc, 'Note').flatMap(note => text(note) ?? []),
				lines: children(root, cac, credit ? 'CreditNoteLine' : 'InvoiceLine').map(line => {
					const item = child(line, cac, 'Item')
					const quantity = child(line, cbc, credit ? 'CreditedQuantity' : 'InvoicedQuantity')
					const price = child(line, cac, 'Price')
					const basis = child(price, cbc, 'BaseQuantity')
					return {
						id: value(line, cbc, 'ID'),
						name: value(item, cbc, 'Name'),
						description: value(item, cbc, 'Description'),
						quantity: text(quantity),
						unit: quantity?.getAttribute('unitCode') || undefined,
						unitPrice: money(price, cbc, 'PriceAmount', currency),
						priceBasis: text(basis),
						priceBasisUnit: basis?.getAttribute('unitCode') || undefined,
						net: money(line, cbc, 'LineExtensionAmount', currency),
						taxRate: value(child(item, cac, 'ClassifiedTaxCategory'), cbc, 'Percent'),
					}
				}),
				taxes: children(taxTotal, cac, 'TaxSubtotal').map(tax => {
					const category = child(tax, cac, 'TaxCategory')
					return {
						category: value(category, cbc, 'ID'),
						rate: value(category, cbc, 'Percent'),
						basis: money(tax, cbc, 'TaxableAmount', currency),
						amount: money(tax, cbc, 'TaxAmount', currency),
						exemption: value(category, cbc, 'TaxExemptionReason'),
					}
				}),
				totals: {
					net: money(totals, cbc, 'TaxExclusiveAmount', currency),
					tax: money(taxTotal, cbc, 'TaxAmount', currency),
					gross: money(totals, cbc, 'TaxInclusiveAmount', currency),
					prepaid: money(totals, cbc, 'PrepaidAmount', currency),
					due: money(totals, cbc, 'PayableAmount', currency),
					allowance: money(totals, cbc, 'AllowanceTotalAmount', currency),
					charge: money(totals, cbc, 'ChargeTotalAmount', currency),
				},
				payment: {
					means: value(means, cbc, 'PaymentMeansCode'),
					reference: value(means, cbc, 'PaymentID'),
					terms: value(child(root, cac, 'PaymentTerms'), cbc, 'Note'),
					accountId: value(account, cbc, 'ID'),
					accountName: value(account, cbc, 'Name'),
					bankIdentifier:
						value(child(child(account, cac, 'FinancialInstitutionBranch'), cac, 'FinancialInstitution'), cbc, 'ID') ??
						value(child(account, cac, 'FinancialInstitutionBranch'), cbc, 'ID'),
				},
			},
		}
	}
	return { kind: 'xml', source, rootName: root.tagName }
}

function decodeXml(bytes: Uint8Array): string {
	try {
		let encoding = 'utf-8'
		if (
			(bytes[0] === 0xff && bytes[1] === 0xfe) ||
			(bytes[0] === 0x3c && bytes[1] === 0 && bytes[2] === 0x3f && bytes[3] === 0)
		)
			encoding = 'utf-16le'
		else if (
			(bytes[0] === 0xfe && bytes[1] === 0xff) ||
			(bytes[0] === 0 && bytes[1] === 0x3c && bytes[2] === 0 && bytes[3] === 0x3f)
		)
			encoding = 'utf-16be'
		else {
			const declaration = new TextDecoder('windows-1252')
				.decode(bytes.subarray(0, 512))
				.match(/^\s*(?:ï»¿)?<\?xml\s[^?]*encoding\s*=\s*['"]([^'"]+)['"]/i)
			if (declaration) encoding = declaration[1]
		}
		return new TextDecoder(encoding, { fatal: true }).decode(bytes)
	} catch {
		throw new XmlPreviewError('unreadable')
	}
}

/** Unsubscription cancels both the fetch and its bounded response reader. */
export function xmlDocument(src: string): Observable<XmlDocument> {
	return fromFetch(src, {
		selector: response =>
			new Observable<XmlDocument>(subscriber => {
				if (!response.ok) {
					subscriber.error(new XmlPreviewError('request'))
					return
				}
				if (Number(response.headers.get('content-length')) > limit) {
					subscriber.error(new XmlPreviewError('too-large'))
					return
				}
				if (!response.body) {
					subscriber.error(new XmlPreviewError('unreadable'))
					return
				}
				const reader = response.body.getReader()
				const chunks: Uint8Array[] = []
				let size = 0
				function read(): void {
					reader
						.read()
						.then(({ done, value }) => {
							if (subscriber.closed) return
							if (done) {
								const bytes = new Uint8Array(size)
								let offset = 0
								for (const chunk of chunks) {
									bytes.set(chunk, offset)
									offset += chunk.byteLength
								}
								subscriber.next(parseXmlDocument(decodeXml(bytes)))
								subscriber.complete()
							} else {
								size += value.byteLength
								if (size > limit) throw new XmlPreviewError('too-large')
								chunks.push(value)
								read()
							}
						})
						.catch(error => subscriber.error(error))
				}
				read()
				return () => {
					void reader.cancel().catch(() => undefined)
				}
			}),
	}).pipe(
		timeout(15_000),
		catchError(error => throwError(() => (error instanceof XmlPreviewError ? error : new XmlPreviewError('request')))),
	)
}
